import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { MatSelectChange } from '@angular/material/select';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import {
  HANDOVER_NOTIFICATION_SELECT_OTHER_OPTIONS,
  HANDOVER_NOTIFICATION_TYPE_COMMENT,
  HANDOVER_NOTIFICATION_TYPE_COMMENT_OFF,
  HANDOVER_NOTIFICATION_TYPE_FILE,
  HANDOVER_NOTIFICATION_TYPE_FILE_WITH_TOGGLE,
  HANDOVER_NOTIFICATION_TYPE_RADIO,
  HANDOVER_NOTIFICATION_TYPE_SELECT,
  HANDOVER_NOTIFICATION_TYPE_TOGGLE,
  HANDOVER_NOTIFICATION_WELL_CONTROL_EVENT_ID,
  HANDOVER_NOTIFICATION_WELL_TYPE_OTHER_IDS,
  HANDOVER_TYPE_AREA_REASON_4_CWO_TO_OPE_ID,
  HANDOVER_TYPE_HO2_ID,
  HANDOVER_TYPE_HO3_ID,
  HANDOVER_TYPE_HOAC_ID,
  HANDOVER_TYPE_HOEC_ID,
  HANDOVER_TYPE_HO1_ID,
  HANDOVER_TYPE_HOG_ID,
  PERMISSION_ENGINEERING_COMPLETION,
} from 'src/app/handover-constants';
import { Handover } from 'src/app/interfaces/handover';
import {
  Notification,
  NotificationAttachment,
  NotificationValue,
  ProductionType,
  WellType,
} from 'src/app/interfaces/notifications';
import { HandoverService } from 'src/app/services/handover.service';
import { NotificationService } from 'src/app/services/notification.service';
import { UserService } from 'src/app/services/user.service';
import { SectionTouched } from '../../shared/section-touched.class';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-handover-detail-notifications',
  templateUrl: './handover-detail-notifications.component.html',
  styleUrls: ['./handover-detail-notifications.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class HandoverDetailNotificationsComponent
  extends SectionTouched
  implements OnInit
{
  @Input() handover: Handover | undefined;
  @Input() readOnlyContent!: boolean;

  isLoading = false;
  displayWellTypeSelector = false;
  wellTypes: WellType[] = [];
  productionTypes: ProductionType[] = [];
  chosenProductionType: ProductionType = {} as ProductionType;
  originalChosenProductionType: ProductionType = {} as ProductionType;
  originalChosenWellType: WellType = {} as WellType;
  chosenWellType: WellType = {} as WellType;
  originalNotificationsAttachment: Notification[] | undefined = [];
  displayOtherWellTypeInput = false;
  otherWellTypeInput = '';
  displayNotifications = false;
  notifications: Notification[] = [];
  groupedNotifications: { [key: string]: Notification[] } = {};
  groupedNotificationsKeys: string[] = [];
  originalNotificationValues: NotificationValue[] = [];
  notificationTypeToggle = HANDOVER_NOTIFICATION_TYPE_TOGGLE;
  notificationTypeComment = HANDOVER_NOTIFICATION_TYPE_COMMENT;
  notificationTypeCommentOff = HANDOVER_NOTIFICATION_TYPE_COMMENT_OFF;
  notificationTypeFile = HANDOVER_NOTIFICATION_TYPE_FILE;
  notificationTypeFileWithToggle = HANDOVER_NOTIFICATION_TYPE_FILE_WITH_TOGGLE;
  notificationTypeRadio = HANDOVER_NOTIFICATION_TYPE_RADIO;
  notificationTypeSelect = HANDOVER_NOTIFICATION_TYPE_SELECT;
  otherWellTypes = HANDOVER_NOTIFICATION_WELL_TYPE_OTHER_IDS;
  otherOptionValues = HANDOVER_NOTIFICATION_SELECT_OTHER_OPTIONS;
  notification53 = -1;
  keepNotificationValue = true;
  handoverTypeHOEC = HANDOVER_TYPE_HOEC_ID;
  handoverTypeHOAC = HANDOVER_TYPE_HOAC_ID;
  handoverType3 = HANDOVER_TYPE_HO3_ID;
  handoverType1 = HANDOVER_TYPE_HO1_ID;
  handoverTypeHOG = HANDOVER_TYPE_HOG_ID;
  isUserFromEngineering = false;
  showNotification = true;
  notificationWellControlEvent: Notification | undefined;
  originalNotificationValueWellControlEvent: NotificationValue =
    {} as NotificationValue;
  dhoApiUrl = `${environment.urlBackend}/dhoapi`;

  constructor(
    public handoverService: HandoverService,
    public notificationService: NotificationService,
    private userService: UserService
  ) {
    super();
  }

  ngOnInit(): void {
    if (HANDOVER_TYPE_HO3_ID == this.handover?.handoverTypeId) {
      this.isUserFromEngineering = this.userService.hasPermission(
        PERMISSION_ENGINEERING_COMPLETION
      );
      if (this.isUserFromEngineering) {
        this.retrieveNotificationWellControlEvent();
      }
    }
    if (
      this.handover?.handoverId &&
      ([
        HANDOVER_TYPE_HO2_ID,
        HANDOVER_TYPE_HO3_ID,
        HANDOVER_TYPE_HO1_ID,
        HANDOVER_TYPE_HOG_ID,
      ].includes(Number(this.handover?.handoverTypeId)) ||
        this.handover?.handoverTypeAreaReasonId ==
          HANDOVER_TYPE_AREA_REASON_4_CWO_TO_OPE_ID)
    ) {
      this.retrieveWellTypes();
    } else {
      this.retrieveNotificationsKeepingValue();
    }
    if (this.handover?.handoverId) {
      this.retriveProductionTypes();
    }
  }

  retriveProductionTypes(): void {
    if (!this.handover?.handoverId) {
      return;
    }
    this.isLoading = true;
    this.handoverService
      .getProductionTypes(this.handover?.handoverId)
      .subscribe((productionTypes) => {
        this.productionTypes = productionTypes;
        this.isLoading = false;
        // Determine if there is an already chosen production type...
        this.chosenProductionType =
          this.productionTypes.find((value) => {
            return true === value.chosen;
          }) || this.chosenProductionType;
        this.originalChosenProductionType = this.chosenProductionType;
      });
  }

  retrieveWellTypes(): void {
    if (!this.handover?.handoverId) {
      return;
    }
    this.isLoading = true;
    this.handoverService
      .getWellTypes(this.handover?.handoverId)
      .subscribe((wellTypes) => {
        this.wellTypes = wellTypes;
        this.displayWellTypeSelector = true;
        this.isLoading = false;
        // Determine if there is an already chosen well type...
        this.chosenWellType =
          this.wellTypes.find((value) => {
            return true === value.chosen;
          }) || this.chosenWellType;
        this.originalChosenWellType = this.chosenWellType;
        if (this.chosenWellType.wellTypeId) {
          // ...and make things happen for that choice.
          this.displayOtherWellTypeInput = this.otherWellTypes.includes(
            this.chosenWellType.wellTypeId
          );
          this.otherWellTypeInput = this.chosenWellType.otherValue || '';
          this.retrieveNotificationsKeepingValue();
        } else {
          if (
            this.handover?.handoverTypeId == this.handoverType1 ||
            this.handover?.handoverTypeId == this.handoverTypeHOG
          ) {
            this.retrieveNotificationsKeepingValue();
          }
        }
      });
  }

  compareObjectsWellType(wellType1: WellType, wellType2: WellType): boolean {
    return wellType1.wellTypeId === wellType2.wellTypeId;
  }

  onChangeWellType(value: WellType): void {
    this.displayOtherWellTypeInput = this.otherWellTypes.includes(
      value.wellTypeId
    );
    this.chosenWellType = value;
    this.emitPendingChanges(
      !this.numberValuesAreEqual(
        this.chosenWellType.wellTypeId,
        this.originalChosenWellType.wellTypeId
      )
    );
    if (
      this.handover?.handoverTypeId != this.handoverType1 &&
      this.handover?.handoverTypeId != this.handoverTypeHOG
    ) {
      this.retrieveNotificationsWithoutKeepingValue();
    }
  }

  onChangeOtherWellTypeInput(value: string): void {
    this.otherWellTypeInput = value;
    if (this.originalChosenWellType.otherValue) {
      this.emitPendingChanges(
        !this.stringValuesAreEqual(
          this.otherWellTypeInput,
          this.originalChosenWellType.otherValue
        )
      );
    }
  }

  save(): void {
    this.saveWellType().subscribe((saved) => {
      if (saved) {
        this.saveNotifications();
      }
    });
    this.saveProductionType().subscribe();
  }

  saveWellType(): Observable<boolean> {
    if (!this.handover?.handoverId) {
      return of(false);
    }
    if (!this.displayWellTypeSelector || !this.canUserChooseWellType()) {
      // Skip this operation with positive result because the
      // well type selector is not in use by the notifications
      // of the current handover type.
      return of(true);
    }
    if (
      this.displayOtherWellTypeInput &&
      !this.otherWellTypeInput &&
      this.showNotification
    ) {
      this.notificationService.notify(
        'Por favor, ingrese nombre o identificador para el tipo de pozo "Otro".',
        false
      );
      return of(false);
    }
    this.isLoading = true;
    return this.handoverService
      .saveWellType(
        this.handover?.handoverId,
        this.chosenWellType.wellTypeId,
        this.otherWellTypeInput
      )
      .pipe(
        tap((hasBeenSaved: boolean) => {
          this.isLoading = false;
          if (!hasBeenSaved) {
            this.notificationService.notify(
              'Ocurrió un error al seleccionar el tipo de pozo',
              false
            );
            return;
          }
        })
      );
  }

  retrieveFiles(): void {
    if (this.handover?.handoverId) {
      this.isLoading = true;
      this.handoverService
        .getFiles(this.handover?.handoverId)
        .subscribe((file) => {
          console.log(file);
        });
    }
  }

  retrieveNotificationsKeepingValue(): void {
    this.keepNotificationValue = true;
    this.retrieveNotifications();
  }

  retrieveNotificationsWithoutKeepingValue(): void {
    this.keepNotificationValue = false;
    this.retrieveNotifications();
  }

  retrieveNotifications(): void {
    if (this.handover?.handoverId) {
      this.isLoading = true;
      let wellType = null;
      if (
        this.handover.handoverTypeId != this.handoverType1 &&
        this.handover.handoverTypeId != this.handoverTypeHOG
      ) {
        wellType = this.chosenWellType?.wellTypeId;
      }
      this.handoverService
        .getNotifications(this.handover?.handoverId, wellType)
        .subscribe((notifications) => {
          if (this.keepNotificationValue) this.originalNotificationValues = [];
          this.notifications = notifications.map((notification, index) => {
            const mappedNotification: Notification = notification;
            if (
              !mappedNotification.value ||
              (!this.keepNotificationValue &&
                this.originalChosenWellType !== this.chosenWellType &&
                this.isUserResponsibleOfNotification(mappedNotification))
            ) {
              mappedNotification.value = {
                notificationId: notification.notificationId,
                activeSelected: false,
                touched: false,
              } as NotificationValue;
            }
            mappedNotification.originalIndex = index;
            if (this.keepNotificationValue) {
              this.originalNotificationValues.push(
                this.copyNotificationValue(mappedNotification.value)
              );
            }
            return mappedNotification;
          });
          // TODO: REFACTORIZAR EL HARCODE DE ESTAS NOTIFICACIONES
          this.notification53 = this.notifications.findIndex(
            (n) => n.notificationId == 53
          );
          this.groupNotifications();
          this.displayNotifications = notifications.length > 0;
          if (this.keepNotificationValue) {
            const notificationsWithAttachment: Notification[] | undefined =
              this.getOriginalNotificationAttachment();
            this.originalNotificationsAttachment = notificationsWithAttachment;
          }
          this.isLoading = false;
        });
    }
  }

  private copyNotificationValue(
    original: NotificationValue
  ): NotificationValue {
    const copy = {
      notificationId: original.notificationId,
      activeSelected: original.activeSelected,
      value: original.value,
      attachment: original.attachment,
      alert: original.alert,
      touched: original.touched,
      otherTriggered: original.otherTriggered,
      otherValue: original.otherValue,
    } as NotificationValue;
    return copy;
  }

  /**
   * Put notifications in the groupedNotifications array,
   * grouping them by a key that is the notificationTopic name.
   * Starts from the second notification, because the first one
   * is expected to be on a group apart.
   * TODO: REFACTORIZAR EL HARCODE DE ESTAS NOTIFICACIONES
   */
  groupNotifications(): void {
    if (
      this.notifications.length > 1 ||
      this.handover?.handoverTypeId == this.handoverTypeHOEC ||
      this.handover?.handoverTypeId == this.handoverTypeHOAC
    ) {
      this.groupedNotifications = {};
      for (
        let i =
          this.handover?.handoverTypeId == this.handoverTypeHOEC ||
          this.handover?.handoverTypeId == this.handoverTypeHOAC
            ? 0
            : 1;
        i < this.notifications.length;
        i++
      ) {
        const element = this.notifications[i];
        // 53 notification is like first one notification, then we exclude it from grouping.
        if (element.notificationId != 53) {
          if (element.notificationTopic != null) {
            const key = element.notificationTopic?.topicName;
            if (!this.groupedNotifications[key]) {
              this.groupedNotifications[key] = [];
            }
            this.groupedNotifications[key].push(element);
          }
        }
      }
      this.groupedNotificationsKeys = Object.keys(this.groupedNotifications);
    }
  }

  saveNotifications(): void {
    this.isLoading = true;
    const postNotifications: NotificationValue[] = [];

    let deleteNotifications = false;

    deleteNotifications =
      this.originalChosenWellType !== this.chosenWellType ? true : false;

    if (this.notifications.length > 0) {
      // TODO: REFACTORIZAR EL HARCODE DE ESTAS NOTIFICACIONES
      // Collect the standalone notification
      if (
        this.isUserResponsibleOfNotification(this.notifications[0]) &&
        (this.notifications[0].value.activeSelected ||
          (this.notifications[0].required &&
            this.notificationTypeFile ==
              this.notifications[0].notificationType) ||
          true === this.notifications[0].value.touched)
      ) {
        postNotifications.push(this.notifications[0].value);
        postNotifications[0].notificationId =
          this.notifications[0].notificationId;
        // Unset properties that are not allowed in the request
        delete postNotifications[0].touched;
        delete postNotifications[0].otherTriggered;
      }

      // Collect the standalone notification 53
      if (
        this.isUserResponsibleOfNotification(
          this.notifications[this.notification53]
        ) &&
        this.notification53 > -1 &&
        (this.notifications[this.notification53].value.activeSelected ||
          (this.notifications[this.notification53].required &&
            this.notificationTypeFile ==
              this.notifications[this.notification53].notificationType) ||
          true === this.notifications[this.notification53].value.touched)
      ) {
        postNotifications.push(this.notifications[this.notification53].value);
        postNotifications[postNotifications.length - 1].notificationId =
          this.notifications[this.notification53].notificationId;
        // Unset properties that are not allowed in the request
        delete postNotifications[postNotifications.length - 1].touched;
        delete postNotifications[postNotifications.length - 1].otherTriggered;
      }
    }
    // Collect notificationWellControlEvent
    if (
      this.notificationWellControlEvent &&
      this.isUserResponsibleOfNotification(this.notificationWellControlEvent) &&
      (this.notificationWellControlEvent.value.activeSelected ||
        (this.notificationWellControlEvent.required &&
          this.notificationTypeFile ==
            this.notificationWellControlEvent.notificationType) ||
        true === this.notificationWellControlEvent.value.touched)
    ) {
      postNotifications.push(this.notificationWellControlEvent.value);
      postNotifications[postNotifications.length - 1].notificationId =
        this.notificationWellControlEvent.notificationId;
      // Unset properties that are not allowed in the request
      delete postNotifications[postNotifications.length - 1].touched;
      delete postNotifications[postNotifications.length - 1].otherTriggered;
    }

    // Collect groupedNotifications.
    if (this.notifications.length > 2) {
      for (let i = 0; i < this.groupedNotificationsKeys.length; i++) {
        const key = this.groupedNotificationsKeys[i];
        for (
          let index = 0;
          index < this.groupedNotifications[key].length;
          index++
        ) {
          const notification = this.groupedNotifications[key][index];
          const notificationValue = notification.value;
          // Only push those that have been activated or are required
          // files/selects/radio or have benn marked as alert.
          if (
            this.isUserResponsibleOfNotification(notification) &&
            (notificationValue.activeSelected ||
              (notification.required &&
                [
                  this.notificationTypeFile,
                  this.notificationTypeSelect,
                  this.notificationTypeRadio,
                ].includes(notification.notificationType)) ||
              true === notificationValue.touched)
          ) {
            notificationValue.notificationId =
              this.groupedNotifications[key][index].notificationId;
            notificationValue.activeSelected =
              notificationValue.activeSelected || false;
            // Unset properties that are not allowed in the request
            delete notificationValue.touched;
            delete notificationValue.otherTriggered;
            postNotifications.push(notificationValue);
          }
        }
      }
    }

    if (this.handover?.handoverId) {
      this.saveNotificationsAndUpdateNotificationsWithAttachment(
        this.handover?.handoverId,
        postNotifications,
        deleteNotifications
      );
    }
  }

  saveNotificationsAndUpdateNotificationsWithAttachment(
    handoverId: number,
    postNotifications: NotificationValue[],
    deleteNotifications: boolean
  ): void {
    this.handoverService
      .saveNotifications(handoverId, postNotifications, deleteNotifications)
      .subscribe(async (ok) => {
        if (ok) {
          this.originalNotificationValues = [];
          this.notifications.map((notification) => {
            const mappedNotification = notification;
            if (!mappedNotification.value) {
              mappedNotification.value = {
                notificationId: notification.notificationId,
                activeSelected: false,
                touched: false,
              } as NotificationValue;
            }
            this.originalNotificationValues.push(
              this.copyNotificationValue(mappedNotification.value)
            );
          });
          this.originalChosenWellType = this.chosenWellType;
          if (this.notificationWellControlEvent?.value)
            this.originalNotificationValueWellControlEvent =
              this.copyNotificationValue(
                this.notificationWellControlEvent.value
              );

          if (deleteNotifications && this.originalNotificationsAttachment) {
            this.originalNotificationsAttachment.forEach(
              async (originalNotificationAttachment) => {
                await this.deleteAttachment(originalNotificationAttachment)
                  .then(() => {
                    const indexToRemove =
                      this.originalNotificationsAttachment?.findIndex(
                        (originalNotificationAttachmentToDelete) =>
                          originalNotificationAttachmentToDelete.notificationId ===
                          originalNotificationAttachment.notificationId
                      );
                    if (indexToRemove)
                      this.originalNotificationsAttachment?.splice(
                        indexToRemove,
                        1
                      );
                  })
                  .catch((error) => {
                    this.notificationService.notify(error, false);
                    this.isLoading = false;
                    return;
                  });
              }
            );
          }
          const notifications = this.getNotificationAttachment();
          if (notifications) {
            notifications.forEach(async (notification, index) => {
              await this.createAttachment(notification)
                .then(() => {
                  this.originalNotificationsAttachment?.push(notification);
                  if (index === notifications.length - 1) {
                    this.notificationService.notify(
                      'Notificaciones guardadas satisfactoriamente',
                      true
                    );
                  }
                  this.emitPendingChanges(false);
                  this.isLoading = false;
                })
                .catch((error) => {
                  this.notificationService.notify(error, false);
                  this.isLoading = false;
                  return;
                });
            });
          } else {
            this.notificationService.notify(
              'Notificaciones guardadas satisfactoriamente',
              true
            );
            this.emitPendingChanges(false);
            this.isLoading = false;
          }
        } else {
          this.notificationService.notify(
            'Ocurrió un error al guardar las notificaciones',
            false
          );
          this.isLoading = false;
        }
        /* Para el caso de HO3 refresco el tipo de pozo y las notificaciones ya que las mismas pudieron ser modificadas por el ingeniero u operativo*/
        if (HANDOVER_TYPE_HO3_ID == this.handover?.handoverTypeId) {
          this.retrieveWellTypes();
        }
      });
  }

  /**
   * Helper to decide wether a notification is properly filled or not,
   * according to its type, current value, mandatory status and other reasons.
   * @param notification that we want to check
   * @returns
   */
  notificationIsNotCompleted(notification: Notification): boolean {
    let isNotCompleted = false;
    switch (notification.notificationType) {
      case this.notificationTypeToggle:
        break;
      case this.notificationTypeSelect:
        isNotCompleted = notification.required && !notification.value.value;
        break;
      case this.notificationTypeRadio:
        isNotCompleted =
          notification.required &&
          !notification.value.value &&
          !notification.value.activeSelected;
        break;
      case this.notificationTypeFile:
        isNotCompleted =
          (notification.required || notification.value.activeSelected) &&
          (!notification.value.attachment?.filePath ||
            !notification.value.value);
        break;
      default:
        isNotCompleted =
          notification.required &&
          notification.value.activeSelected &&
          !notification.value.value;
        break;
    }
    return isNotCompleted;
  }

  getNotificationAttachment(): Notification[] | undefined {
    const notifications: Notification[] | undefined = this.notifications.filter(
      (notification) =>
        (notification.notificationType == this.notificationTypeFile ||
          notification.notificationType ==
            this.notificationTypeFileWithToggle) &&
        notification.fileToUpload
    );
    if (notifications.length === 0) return undefined;
    return notifications;
  }

  getOriginalNotificationAttachment(): Notification[] | undefined {
    const notification: Notification[] | undefined = this.notifications.filter(
      (notification) =>
        notification.notificationType == this.notificationTypeFile ||
        notification.notificationType == this.notificationTypeFileWithToggle
    );
    return notification;
  }

  getNameFile(attachment: NotificationAttachment | undefined): string {
    if (!attachment?.fileOriginalName) {
      const index = attachment?.filePath.lastIndexOf('.');
      return index !== -1
        ? 'Planilla_de_despresurización' + attachment?.filePath.slice(index)
        : '';
    } else return attachment.fileOriginalName;
  }

  onFileSelected(event: any, notification: Notification): void {
    this.emitPendingChanges(true);
    const file: File = event.target.files[0];
    notification.fileToUpload = file;
  }

  async onFileDeleted(notification: Notification): Promise<void> {
    this.isLoading = true;
    await this.deleteAttachment(notification).catch((error) => {
      this.notificationService.notify(error, false);
    });
    this.isLoading = false;
  }

  deniedDownload(event: any): void {
    event.preventDefault();
    this.notificationService.notify(
      'Para descargar el archivo, previamente debe guardar la sección',
      false
    );
  }

  async createAttachment(notification: Notification): Promise<void | string> {
    return new Promise<void | string>((resolve, reject) => {
      if (this.handover?.handoverId && notification.fileToUpload) {
        this.isLoading = true;
        const formData = new FormData();
        formData.append('file', notification.fileToUpload);

        this.handoverService
          .saveNotificationAttachment(
            this.handover?.handoverId,
            notification.notificationId,
            formData
          )
          .subscribe(
            (uploaded: NotificationAttachment[]) => {
              if (notification.value) {
                notification.value.attachment = uploaded[0];
                notification.value.activeSelected = true;
              } else {
                notification.value = {
                  notificationId: notification.notificationId,
                  activeSelected: true,
                  attachment: uploaded[0],
                } as NotificationValue;
              }
              delete notification.fileToUpload;
              resolve();
            },
            (response: HttpErrorResponse) => {
              reject(response.error.message);
            }
          );
      }
    });
  }

  async deleteAttachment(notification: Notification): Promise<void | string> {
    return new Promise<void | string>((resolve, reject) => {
      if (!this.handover?.handoverId || !notification.value?.attachment) {
        delete notification.fileToUpload;
        resolve();
      }

      this.isLoading = true;
      notification.value.attachment;
      if (notification.value.attachment?.fileId && this.handover?.handoverId)
        this.handoverService
          .deleteNotificationAttachment(
            this.handover?.handoverId,
            notification.notificationId,
            notification.value.attachment.fileId,
            notification.value.attachment.filePath
          )
          .subscribe(
            (deleted: boolean) => {
              if (deleted) {
                delete notification.value.attachment;
              }
              resolve();
            },
            (response: HttpErrorResponse) => {
              reject(response.error.message);
            }
          );
    });
  }

  onTextNotificationValueChanges(
    notification: Notification,
    value: string,
    originalTextValue: string
  ): void {
    notification.value.touched = true;
    notification.value.value = value;
    this.onSectionTouched('notifications');
    this.emitPendingChanges(
      !this.stringValuesAreEqual(notification.value.value, originalTextValue)
    );
  }

  onTextNotificationOtherValueChanges(
    notification: Notification,
    otherValue: string
  ): void {
    notification.value.touched = true;
    notification.value.otherValue = otherValue;
    this.onSectionTouched('notifications');
    this.emitPendingChanges(
      !this.stringValuesAreEqual(
        notification.value.otherValue,
        this.originalNotificationValues[notification.originalIndex]
          .otherValue || ''
      )
    );
  }

  onToggleNotification(
    notification: Notification,
    originalNotificationValue: NotificationValue
  ): void {
    notification.value.touched = true;
    this.onSectionTouched('notifications');
    this.emitPendingChanges(
      !this.notificationValuesAreEqual(
        notification.value,
        originalNotificationValue
      )
    );
  }

  onToggleRadioNotification(
    notification: Notification,
    originalNotificationValue: NotificationValue
  ): void {
    this.onSectionTouched('notifications');
    if (notification.value.activeSelected) {
      notification.value.value = '';
    }
    this.emitPendingChanges(
      !this.notificationValuesAreEqual(
        notification.value,
        originalNotificationValue
      )
    );
  }

  onSelectRadioNotificationValue(
    notification: Notification,
    originalNotificationValue: NotificationValue
  ): void {
    this.onSectionTouched('notifications');
    notification.value.activeSelected = false;
    this.emitPendingChanges(
      !this.notificationValuesAreEqual(
        notification.value,
        originalNotificationValue
      )
    );
  }

  notificationAlertText(notification: Notification): string {
    if (this.notificationTypeRadio == notification.notificationType) {
      return notification.notificationLabelInactive;
    }
    return notification.notificationLabelActive;
  }

  onNotificationAlertChanges(notification: Notification): void {
    notification.value.alert = !notification.value.alert;
    notification.value.touched = true;
    this.onSectionTouched('notifications');
    this.emitPendingChanges(
      !this.notificationValuesAreEqual(
        notification.value,
        this.originalNotificationValues[notification.originalIndex]
      )
    );
  }

  onSelectNotificationChanges(
    $event: MatSelectChange,
    notification: Notification
  ): void {
    if (HANDOVER_NOTIFICATION_SELECT_OTHER_OPTIONS.includes($event.value)) {
      notification.value.otherTriggered = true;
    } else {
      notification.value.otherTriggered = false;
    }
    notification.value.activeSelected = true;
    this.onSectionTouched('notifications');
    this.emitPendingChanges(
      !this.notificationValuesAreEqual(
        notification.value,
        this.originalNotificationValues[notification.originalIndex]
      )
    );
  }

  notificationValuesAreEqual(
    value1: NotificationValue,
    value2: NotificationValue
  ): boolean {
    return (
      value1.activeSelected === value2.activeSelected &&
      ((!value1.value && !value2.value) || value1.value === value2.value) &&
      value1.attachment === value2.attachment &&
      value1.alert === value2.alert &&
      value1.otherValue === value2.otherValue
    );
  }

  //TODO: moverlo a shared
  numberValuesAreEqual(value1: number, value2: number): boolean {
    return (!value1 && !value2) || value1 === value2;
  }
  //TODO: moverlo a shared
  stringValuesAreEqual(value1: string, value2: string): boolean {
    return (!value1 && !value2) || value1 == value2;
  }

  /* Division de responsabilidades */

  /**
   * Determina si el usuario es responsable de la edición de la notificación dada.
   */
  isUserResponsibleOfNotification(notification: Notification): boolean {
    // Si el presente Handover es un HO3, se controla
    if (this.handover && this.handover.handoverTypeId == HANDOVER_TYPE_HO3_ID) {
      // "Ocurrió evento de Control de Pozo" sólo gestionable con permiso "Completar Ingeniería"
      if (
        HANDOVER_NOTIFICATION_WELL_CONTROL_EVENT_ID ==
        notification.notificationId
      ) {
        return this.isUserFromEngineering;
      }
      // Demás notificaciones no son responsabilidad de ingeniería
      return !this.isUserFromEngineering;
    }
    return true;
  }

  canUserChooseWellType(): boolean {
    return (
      this.handover != undefined &&
      !this.handover.sent &&
      !this.handover?.sentEngineering
    );
  }

  /**
   * Determines wether the given notification is able to be displayed
   * in the context of Responsibility Division between Operations and
   * Engineering inside a Handover Type 3.
   * To be used inside iterator of this.groupedNotifications.
   */
  notificationPassesRDFilter(notification: Notification): boolean {
    return (
      this.handover?.handoverTypeId != this.handoverType3 ||
      !(
        this.isUserFromEngineering &&
        notification.notificationId ==
          HANDOVER_NOTIFICATION_WELL_CONTROL_EVENT_ID
      )
    );
  }

  /**
   * US #3759 - Only for HO3 and engineering users.
   */
  retrieveNotificationWellControlEvent(): void {
    if (this.handover?.handoverId) {
      this.isLoading = true;
      this.handoverService
        .getHandoverNotification(
          this.handover.handoverId,
          HANDOVER_NOTIFICATION_WELL_CONTROL_EVENT_ID
        )
        .subscribe((n) => {
          if (!n.value) {
            n.value = {
              notificationId: HANDOVER_NOTIFICATION_WELL_CONTROL_EVENT_ID,
              activeSelected: false,
              touched: false,
            } as NotificationValue;
          }
          this.notificationWellControlEvent = n;
          this.originalNotificationValueWellControlEvent =
            this.copyNotificationValue(this.notificationWellControlEvent.value);
          this.isLoading = false;
        });
    }
  }

  onChangeProductionType(value: ProductionType): void {
    this.chosenProductionType = value;
    this.emitPendingChanges(
      !this.numberValuesAreEqual(
        this.chosenProductionType.productionTypeId,
        this.originalChosenProductionType.productionTypeId
      )
    );
  }

  compareObjectsProductionType(
    productionType1: ProductionType,
    productionType2: ProductionType
  ): boolean {
    return (
      productionType1.productionTypeId === productionType2.productionTypeId
    );
  }

  saveProductionType(): Observable<boolean> {
    if (!this.handover?.handoverId) {
      return of(false);
    }
    if (!this.canUserChooseWellType()) {
      return of(true);
    }
    this.isLoading = true;
    return this.handoverService
      .saveProductionType(
        this.handover?.handoverId,
        this.chosenProductionType.productionTypeId
      )
      .pipe(
        tap((hasBeenSaved: boolean) => {
          this.isLoading = false;
          if (!hasBeenSaved) {
            this.notificationService.notify(
              'Ocurrió un error al seleccionar el tipo de produccion',
              false
            );
            return;
          }
        })
      );
  }
}
